import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRemoveDriveRoot } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { DriveRootSafe, DriveSafe } from '@vivport/backend/types';
import { useState } from 'react';
import { toast } from 'sonner';
import { DriveSyncPanel } from './DriveSyncPanel';
import { LibraryPanel } from './LibraryPanel';
import { ProjectsPanel } from './ProjectsPanel';
import { RootDialog } from './RootDialog';
import { formatTextReadiness } from './text-readiness';

function summarizeFilters(root: DriveRootSafe): string {
  const clauses: string[] = [];
  if (root.includeExtensions.length > 0) {
    clauses.push(`${root.includeExtensions.map((ext) => ext.toUpperCase()).join(', ')} only`);
  }
  if (root.excludeExtensions.length > 0) {
    clauses.push(`excluding ${root.excludeExtensions.map((ext) => ext.toUpperCase()).join(', ')}`);
  }
  if (root.modifiedAfter) {
    clauses.push(`modified after ${new Date(root.modifiedAfter).toLocaleDateString()}`);
  }
  if (root.maxSizeBytes !== null) {
    clauses.push(`up to ${Math.round(root.maxSizeBytes / (1024 * 1024))} MB`);
  }
  if (root.excludePathGlobs.length > 0) {
    clauses.push(`skip ${root.excludePathGlobs.join(', ')}`);
  }
  return clauses.length > 0 ? clauses.join('; ') : 'all files';
}

type RootDialogState = { open: false } | { open: true; root: DriveRootSafe | null };

type DriveRootsPanelProps = {
  drive: DriveSafe;
};

export function DriveRootsPanel({ drive }: DriveRootsPanelProps) {
  const removeRoot = useRemoveDriveRoot();
  const [rootDialog, setRootDialog] = useState<RootDialogState>({ open: false });

  const handleRemoveRoot = (root: DriveRootSafe) => {
    if (!window.confirm(`Remove root ${root.path}?`)) {
      return;
    }
    removeRoot.mutate(root.id, {
      onSuccess: () => toast.success(`Removed root ${root.path}.`),
      onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to remove the root.')),
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {drive.roots.length === 0
            ? 'No mirrored roots: the folder is read live. Add a root to mirror files into Vivport, file them under projects or tag them as a library.'
            : `${drive.roots.length} root${drive.roots.length === 1 ? '' : 's'} mirrored`}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => setRootDialog({ open: true, root: null })}>
          Add root
        </Button>
      </div>

      {drive.roots.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Path</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Filters</TableHead>
              <TableHead>Background extraction</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {drive.roots.map((root) => (
              <TableRow key={root.id}>
                <TableCell className="font-mono text-xs">{root.path}</TableCell>
                <TableCell>{root.role}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{summarizeFilters(root)}</TableCell>
                <TableCell>
                  <Badge variant={root.backgroundExtraction ? 'default' : 'outline'}>
                    {root.backgroundExtraction ? 'On' : 'Off'}
                  </Badge>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatTextReadiness({
                      eligible: root.textEligibleCount,
                      ready: root.textReadyCount,
                      backgroundExtraction: root.backgroundExtraction,
                    })}
                  </p>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setRootDialog({ open: true, root })}>
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveRoot(root)}
                      disabled={removeRoot.isPending}
                    >
                      Remove
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {drive.roots.map((root) =>
        root.role === 'PROJECTS' ? (
          <div key={`root-${root.id}`} className="border-t pt-3">
            <ProjectsPanel root={root} driveId={drive.id} driveName={drive.name} />
          </div>
        ) : root.role === 'LIBRARY' ? (
          <div key={`root-${root.id}`} className="border-t pt-3">
            <LibraryPanel root={root} />
          </div>
        ) : null
      )}

      <div className="border-t pt-3">
        <p className="mb-2 text-sm font-medium">Sync</p>
        <DriveSyncPanel drive={drive} />
      </div>

      {rootDialog.open ? (
        <RootDialog
          key={`${drive.id}-${rootDialog.root?.id ?? 'new'}`}
          driveId={drive.id}
          driveName={drive.name}
          root={rootDialog.root}
          open
          onOpenChange={(open) => {
            if (!open) {
              setRootDialog({ open: false });
            }
          }}
        />
      ) : null}
    </div>
  );
}
